import { useRef, useState } from 'react';

import Box from '@zenbusiness/zui/Box';
import IconButton from '@zenbusiness/zui/IconButton';
import MuiMenuItem from '@zenbusiness/zui/MenuItem';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import Tooltip from '@zenbusiness/zui/Tooltip';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import useBreakpoints from '#client/hooks/useBreakpoints';

import { StepItemStyle, TooltipStyle } from '../styles';

const DEFAULT_DISPLAY_TIME = 800;

const MenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'isBold'
})(StepItemStyle);
const TooltipStyled = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    aria-label="copy button"
    classes={{ popper: className }}
  />
))(TooltipStyle);

export default function StepItem({ copyText, title, texts, callback }) {
  const copyButtonRef = useRef();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const [showTooltip, setShowTooltip] = useState(false);

  const placement = isDesktop && texts.some((text) => text.length >= 16) ? 'top' : 'right';

  const handleClick = () => {
    copy(copyText);

    setShowTooltip(true);
    copyButtonRef.current.blur();

    setTimeout(() => {
      setShowTooltip(false);
    }, DEFAULT_DISPLAY_TIME);
    if (typeof callback === 'function') {
      callback();
    }
  };

  return (
    <MenuItem>
      <Stack spacing="8px">
        <Text
          color="secondary"
          size="xs"
          weight="medium"
        >
          {title}
        </Text>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing="8px"
        >
          <Box>
            {texts.map((text) => (
              <Text
                color={showTooltip ? 'green' : 'primary'}
                component="p"
                key={text}
                size="md"
                weight="medium"
              >
                {text}
              </Text>
            ))}
          </Box>
          <TooltipStyled
            open={showTooltip}
            placement={placement}
            title="Copied"
          >
            <IconButton
              label="copy-button"
              onClick={handleClick}
              ref={copyButtonRef}
            >
              <ContentCopyIcon
                color={showTooltip ? 'text.green' : 'primary'}
                fontSize="small"
              />
            </IconButton>
          </TooltipStyled>
        </Stack>
      </Stack>
    </MenuItem>
  );
}

StepItem.propTypes = {
  copyText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  texts: PropTypes.arrayOf(PropTypes.string).isRequired,
  callback: PropTypes.func
};
